import { cloneElement, type ReactElement, useState } from 'react';
import { Button, Popover, UnstyledButton } from '@mantine/core';

import { usePwaUpdateContext } from '@/components/pwa/pwaUpdateContext';
import { I, type IconProps } from '@/components/ri/icons';

import './PWAUpdateIndicator.css';

/** Header pill shown while a newer build is waiting. Never blocks the page; the user refreshes when ready. */
export function PWAUpdateIndicator() {
  const [opened, setOpened] = useState(false);
  const update = usePwaUpdateContext();
  if (!update?.showUpdate) return null;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={300}
      withinPortal
      trapFocus
      shadow="lg"
      radius={12}
      offset={6}
    >
      <Popover.Target>
        <UnstyledButton
          className="pwa-update-trigger"
          onClick={() => setOpened((o) => !o)}
          aria-label="New version ready"
          aria-expanded={opened}
        >
          {cloneElement(I.refresh as ReactElement<IconProps>, { size: 15 })}
          <span className="pwa-update-label">Update</span>
          <span className="pwa-update-dot" aria-hidden />
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown className="pwa-update-dropdown" role="dialog" aria-label="New version ready">
        <div className="pwa-update-title">New version ready</div>
        <p className="pwa-update-text">
          Finish what you're on, then refresh whenever. Nothing changes until you do.
        </p>
        <div className="pwa-update-actions">
          <Button
            size="xs"
            variant="subtle"
            onClick={() => {
              setOpened(false);
              update.snooze();
            }}
          >
            Later
          </Button>
          <Button size="xs" onClick={update.update}>
            Refresh now
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
